import { AccessTime, Check, Delete } from '@mui/icons-material';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TableCell,
	TableRow,
	Box,
	Chip,
	Typography,
	Grid,
	Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { type FC, useState } from 'react';
import apiClient from '../../ApiClient';
import type { TiktokAccount, Video } from '../Schedule/SchedulePage';
import type { RecurringSchedule } from './DashboardPage';
import { ModalVideoPlayer } from '../NewSchedule/VideoPlayer';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
	group: string;
	videos: Video[];
	schedule: RecurringSchedule;
	account: TiktokAccount | undefined;
	onDelete: () => void;
}

export const GroupScheduleRow: FC<Props> = ({ group, videos, schedule, account, onDelete }) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [videosDialogOpen, setVideosDialogOpen] = useState(false);

	const scheduleTimes = [
		schedule.post_time_1,
		schedule.post_time_2,
		schedule.post_time_3,
		schedule.post_time_4,
		schedule.post_time_5,
	]
		.filter(t => t)
		.map(time => {
			const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const today = dayjs().format('YYYY-MM-DD');
			const utcDateTime = dayjs.utc(`${today} ${time}`);
			const localTime = utcDateTime.tz(userTimezone);
			return localTime.format('HH:mm');
		});

	const deleteSchedule = async (id: number) => {
		try {
			await apiClient.delete(`core/recurringschedule/${id}/`);
			onDelete();
		} catch (err) {
			console.error(err);
		} finally {
			setDeleteDialogOpen(false);
		}
	};

	const allVideosPosted =
		videos.length > 0 && videos.length === videos.filter(v => v.posted).length;

	return (
		<>
			<TableRow sx={{ opacity: allVideosPosted ? 0.5 : 1 }}>
				<TableCell>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
						}}
					>
						<Tooltip title="View videos in group">
							<Box
								component="span"
								tabIndex={0}
								role="button"
								onClick={() => setVideosDialogOpen(true)}
								onKeyDown={e => e.key === 'Enter' && setVideosDialogOpen(true)}
								sx={{
									cursor: 'pointer',
									transition: 'all 0.2s ease-in-out',
									'&:hover': {
										textDecoration: 'underline',
										color: 'primary.main',
									},
									'&:focus': {
										outline: 'none',
										boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}`,
									},
								}}
							>
								{group}
							</Box>
						</Tooltip>

						{allVideosPosted && <Check color="success" fontSize="small" />}
					</Box>
				</TableCell>
				<TableCell align="center">{videos.length}</TableCell>
				<TableCell align="center">{videos.filter(v => v.posted).length}</TableCell>
				<TableCell align="center">
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
						{scheduleTimes.map((time: string) => (
							<Chip key={time} label={time} size="small" color="primary" variant="outlined" />
						))}
					</Box>
				</TableCell>
				<TableCell align="center">{account?.username ?? 'N/A'}</TableCell>
				<TableCell align="center">
					<IconButton
						onClick={() => setDeleteDialogOpen(true)}
						children={<Delete />}
						style={{ color: 'grey', opacity: 0.6 }}
					/>
				</TableCell>
			</TableRow>

			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Confirm Delete</DialogTitle>
				<DialogContent>
					Are you sure you want to delete the schedule for group <strong>{group}</strong>?
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
					<Button onClick={() => deleteSchedule(schedule.id)} color="error">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={videosDialogOpen}
				onClose={() => setVideosDialogOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Typography variant="h6" component="div">
						Videos in Group: {group}
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={1}>
						{videos.map(video => (
							<Grid item xs={6} sm={4} md={3} key={video.id}>
								<Box
									sx={{
										p: 0.5,
										border: '1px solid rgba(0, 0, 0, 0.12)',
										borderRadius: 1,
										mb: 1,
										'&:hover': {
											backgroundColor: 'rgba(0, 0, 0, 0.02)',
										},
									}}
								>
									<Box sx={{ position: 'relative' }}>
										<ModalVideoPlayer videoUrl={video.video} />
										<Box
											sx={{
												position: 'absolute',
												top: 4,
												right: 4,
												zIndex: 1,
											}}
										>
											{video.posted ? (
												<Chip
													label={
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
															<span>Posted</span>
															<Check sx={{ fontSize: '16px' }} />
														</Box>
													}
													size="small"
													sx={{
														height: '20px',
														backgroundColor: '#4CAF50',
														'& .MuiChip-label': {
															color: 'white',
															fontWeight: 500,
															display: 'flex',
															alignItems: 'center',
														},
													}}
												/>
											) : (
												<Chip
													label={
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
															<span>Scheduled</span>
															<AccessTime sx={{ fontSize: '16px' }} />
														</Box>
													}
													size="small"
													sx={{
														height: '20px',
														backgroundColor: '#9E9E9E',
														'& .MuiChip-label': {
															color: 'white',
															fontWeight: 500,
															display: 'flex',
															alignItems: 'center',
														},
													}}
												/>
											)}
										</Box>
									</Box>
								</Box>
							</Grid>
						))}
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setVideosDialogOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
